import React, { useState, useEffect } from 'react';
import { 
  Trash2, Settings, LogOut, 
  User, Shield, 
  Lock, Crosshair,
  Bolt, Star, ChevronDown,
  Clock, Bell, HelpCircle,
} from 'lucide-react';
import axios from 'axios';
import Avatar from '../../services/Avatar';
import SecureDeleteSimulator from './SecDel/secloader';
import Sidebar from './SecDel/Sidebar';
import MainContent from './SecDel/MainContent';
import Footer from './SecDel/Footer';
import HelpCenter from './SecDel/Help';
import DeleteConfirmation from './SecDel/DelConfirm';


const performanceData = [
  { name: 'Jan', speed: 65, success: 98 },
  { name: 'Feb', speed: 72, success: 99 },
  { name: 'Mar', speed: 68, success: 97 },
  { name: 'Apr', speed: 75, success: 99 },
  { name: 'May', speed: 80, success: 100 },
  { name: 'Jun', speed: 78, success: 99 },
];

const SecureDeletionInterface = () => { 
  const [fileList, setFileList] = useState([]); 
  const [selectedTarget, setSelectedTarget] = useState('Files');
  const [files, setFiles] = useState([]);
  const [deleteMode, setDeleteMode] = useState('military');
  const [selectedDisks, setSelectedDisks] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]); 
  const [diskList, setDiskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loaderVisible, setLoaderVisible] = useState(false); 
  const [deletionSuccess, setDeletionSuccess] = useState(false); 
  const [isModalOpen, setIsModalOpen] = useState(false); 
  const [currentUser, setCurrentUser] = useState(null);

  const [isConfirmVisible, setConfirmVisible] = useState(false);

    const [confirmationDetails, setConfirmationDetails] = useState({
      type: '',
      deleteMode: ''
    });

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen); // Toggle modal visibility
  };

  /*useEffect(() => {
    const fetchUserProfile = async () => {
        try {
        const response = await axios.get('/users/me', { withCredentials: true });
        setCurrentUser(response.data); // Set user data
        } catch (error) {
        //console.error('Error fetching user profile:', error);
        } 
    };

    fetchUserProfile();
    }, []);*/

    useEffect(() => {
      const controller = new AbortController();
      const fetchUserProfile = async () => {
          try {
              const response = await axios.get('/users/me', { 
                  withCredentials: true,
                  signal: controller.signal // Attach signal to cancel duplicate requests
              });
              setCurrentUser(response.data);
          } catch (error) {
              if (axios.isCancel(error)) {
                  console.log('Request canceled:', error.message);
              } else {
                  console.error('Error fetching user profile:', error);
              }
          }
      };
  
      fetchUserProfile();
  
      return () => controller.abort(); // Cleanup function to cancel request on re-render
  }, []);
  

  const onClose = () => {
    setIsModalOpen(false);
  };

  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );

  const [selectedFolders, setSelectedFolders] = useState([]); // For multiple folders
 
  const [folderList, setFolderList] = useState([]); 
  const [alert, setAlert] = useState(null);
  const [optDuration, setOptDuration] = useState(''); 
  const [deletionStats, setDeletionStats] = useState({
      deletionRate: '',
      totalSize: '',
      filesProcessed: '',
      successRate: '',
      failures: ''
  });

  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const notifications = [
    { id: 1, type: 'success', message: 'Secure deletion completed', time: '2m ago' },
    { id: 2, type: 'warning', message: 'System scan scheduled', time: '1h ago' },
    { id: 3, type: 'info', message: 'New security update available', time: '3h ago' }
  ];

  



  useEffect(() => {
    if (selectedTarget === 'Disk') {
      fetchDiskList();
    } else if (selectedTarget === 'Files') {
      fetchFiles(); // Fetch files when "Files" is selected
    } else if (selectedTarget === 'Folders') {
      fetchFolders(); // Fetch folders when "Folders" is selected
    }
  }, [selectedTarget]);

  const fetchDiskList = async () => {
    setLoading(true);
    try {
      const response = await fetch('/secDel_ls_disk');
      const data = await response.json();
      setDiskList(data.contents);
      setMessage("");
    } catch (error) {
      console.error("Error fetching disk list:", error);
      setMessage("Failed to load disks.");
    } finally {
      setLoading(false);
    }
  };


  const handleProcessClick = () => {
    if (selectedFiles.length === 0 && selectedFolders.length === 0 && selectedDisks.length === 0) {
        return; // Do nothing if no items are selected
    }

    // Get the type and delete mode
    const type = selectedTarget.toLowerCase(); // e.g., 'file', 'folder', or 'disk'

    // Set confirmation details to be passed to DeleteConfirmation component
    setConfirmationDetails({ type, deleteMode });
    // Show confirmation dialog
    setConfirmVisible(true);
};

const handleConfirmation = async (userAgreed) => {
    if (!userAgreed) {
        setConfirmVisible(false);
        return;
    }

    const type = selectedTarget.toLowerCase(); // e.g., 'file', 'folder', or 'disk'

    setConfirmVisible(false); // Close the confirmation dialog

    try {
      // Show loading state
      setIsLoading(true);
      setLoaderVisible(true); // Show the loader
      const startTime = Date.now();

      // Gather selected items
      const itemsToDelete = [
          ...selectedFiles.map(file => ({ fdloc: file.fdloc })),
          ...selectedFolders.map(folder => ({ fdloc: folder.fdloc })),
          ...selectedDisks.map(disk => ({ fdloc: disk.fdloc }))
      ].map(item => ({ type, ...item }));

      // Prepare your request for deletion 
      const response = await fetch('/opt_secDel', {
          method: 'DELETE',
          headers: {
              'Content-Type': 'application/json',
          },
          body: JSON.stringify({
              items: itemsToDelete,
              deleteMode // include the current selected deletion mode
          }),
      });

      const endTime = Date.now();
      const durationMs = endTime - startTime;

      // Handle server response
      if (response.ok) {
          const responseData = await response.json();            
          setDeletionSuccess(responseData.success); 

          setOptDuration(formatDuration(durationMs));

          console.log("Deletion duration (ms):", durationMs);
          console.log("Formatted duration:", formatDuration(durationMs));


          // Update statistics
          const stats = responseData.stats;
          setDeletionStats({
              deletionRate: stats.deletion_rate,
              totalSize: stats.total_size,
              filesProcessed: stats.processed_count,
              successRate: stats.success_rate,
              failures: stats.failures
          });

          // Show success message
          setAlert({
              type: 'success',
              message: `Deletion successful! ${stats.processed_count} items processed with a success rate of ${stats.success_rate}%.`
          });

          
          setSelectedTarget([]);
          setDeleteMode('military')
          // Reset selections
          setSelectedFiles([]);
          setSelectedFolders([]);
          setSelectedDisks([]);

      } else {
          setDeletionSuccess(false);
          setAlert({
              type: 'error',
              message: 'Error deleting items'
          });
      }
  } catch (error) {
      setDeletionSuccess(false);
      setAlert({
          type: 'error',
          message: 'Unexpected error'
      });
      console.error('Unexpected error', error);
  } finally {
      setIsLoading(false); // Reset loading state
      setLoaderVisible(false); // Hide the loader      
  }
};


const formatDuration = (durationMs) => {
  if (durationMs < 1000) return "1s"; // Ensure at least 1 second is shown
  const seconds = Math.floor(durationMs / 1000);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  return `${hours > 0 ? hours + 'h ' : ''}${minutes > 0 ? minutes + 'm ' : ''}${remainingSeconds > 0 ? remainingSeconds + 's' : ''}`.trim();
};


// Define a function to handle alert closing
const handleAlertClose = () => {
  setAlert(null);
};




  
  const selectDisk = (diskToSelect) => {
    setSelectedDisks(prevSelected => {
      const isSelected = prevSelected.some(selected => selected.name === diskToSelect.name);
      if (isSelected) {
        // If already selected, remove it from selection
        return prevSelected.filter(selected => selected.name !== diskToSelect.name);
      } else {
        // If not selected, add it to selection along with fdloc
        return [
          ...prevSelected,
          {
            name: diskToSelect.name,     // Assuming these properties exist on disk           
            fdloc: diskToSelect.fdloc,   // Include fdloc here            
          }
        ];
      }
    });
  };


  const handleFileClick = async (file) => {
    // If it's a file that is selected with a single click, toggle selection
    if (file.is_dir === false) {
      setSelectedFiles(prevSelected => {
        // Check if the file is already selected, if so remove it, otherwise add it
        const isSelected = prevSelected.some(selected => selected.name === file.name);
        if (isSelected) {
          // Remove the selected file
          return prevSelected.filter(selected => selected.name !== file.name);
        } else {
          // Add the file with its fdloc
          return [...prevSelected, { name: file.name, fdloc: file.fdloc }];
        }
      });
      
      // Perform further actions with the file, such as fetching its contents
      // If you want to perform any actions regardless of selection, do them here
    } else {
      // If it is a directory, call the fetch function to load its contents
      await fetchFiles(file.fdloc);
      setSelectedFolders(prevSelected => {
        // Check if the folder is already selected
        const isSelected = prevSelected.some(selected => selected.name === file.name);
        if (isSelected) {
          // Remove the selected folder
          return prevSelected.filter(selected => selected.name !== file.name);
        } else {
          // Add the folder with its fdloc
          return [...prevSelected, { name: file.name, fdloc: file.fdloc }];
        }
      });
    }
  };
  
  let clickTimeout;
  


const handleFolderInteraction = (event, folder) => {
  if (folder.is_dir) { // Check if it's a directory
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      clickTimeout = null;
      handleDoubleClick(folder);
    } else {
      clickTimeout = setTimeout(() => {
        handleSingleClick(folder);
        clickTimeout = null;
      }, 300); // Delay to distinguish single vs double click (300ms)
    }
  } else {
    // Logic for non-folders
    handleSingleClick(folder); // Directly handle single clicks to submit the request
  }
};

const handleSingleClick = async (folder) => {
  if (folder.is_dir) {
    setSelectedFolders(prevSelected => {
      const isSelected = prevSelected.some(selected => selected.name === folder.name); // Check inclusion via name
      if (isSelected) {
        return prevSelected.filter(selected => selected.name !== folder.name); // Remove if already selected
      } else {
        return [...prevSelected, { name: folder.name, fdloc: folder.fdloc }]; // Add folder with fdloc
      }
    }); // Toggle folder selection
  } else {
    await fetchFolders(folder.fdloc); // This should trigger request submission for non-folders
  }
};

const handleDoubleClick = async (folder) => {
  setSelectedFolders(prevSelected => {
    const isSelected = prevSelected.some(selected => selected.name === folder.name); // Check inclusion via name
    if (!isSelected) {
      return [...prevSelected, { name: folder.name, fdloc: folder.fdloc }]; // Optionally add to selected folders on double click
    }
    return prevSelected; // Return unchanged if already selected
  });
  await fetchFolders(folder.fdloc); // Trigger request for new folders
};

  const fetchFiles = async (fileContent) => {
    setLoading(true);
    try {
      const response = await fetch('/secDel_ls_files', {
        method: 'POST',
        body: JSON.stringify({ fdloc: fileContent }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();      
      setFileList(data.contents); // Make sure to use 'contents' instead of 'devices'
      setMessage("");
    } catch (error) {
      //console.error("Error fetching file list:", error);
      setMessage("Failed to load files.");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFolders = async (FolderContent) => {
    setLoading(true);
    try {
      const response = await fetch('/secDel_ls_folders', {
        method: 'POST',
        body: JSON.stringify({ fdloc: FolderContent }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setFolderList(data.contents); // Make sure to use 'contents' instead of 'devices'
      setMessage("");
    } catch (error) {
      //console.error("Error fetching folder list:", error);
      setMessage("Failed to load folders.");
    } finally {
      setLoading(false);
    }
  };
  
  const handleTargetSelection = (targetType) => {
    // Clear selections based on the new target type
    setSelectedTarget(targetType);
   
    // Reset selections according to the new target type
    if (targetType === "Files") {
      setSelectedFolders([]); // Clear selected folders
      setSelectedDisks([]);   // Clear selected disks
    } else if (targetType === "Folders") {
      setSelectedFiles([]);   // Clear selected files
      setSelectedDisks([]);   // Clear selected disks
    } else if (targetType === "Disk") {
      setSelectedFiles([]);    // Clear selected files
      setSelectedFolders([]);   // Clear selected folders
    }
  };

 const handleLogout = async () => {
  try {
    const response = await fetch('/d-auth/logout', {
      method: 'POST', // or 'GET', depending on your backend implementation
      credentials: 'include', // include cookies with the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {       
      
      window.location.href = '/'; 
    } else {
      // If there's an error during logout
      const errorData = await response.json();
      //console.error('Logout failed', errorData);
      alert( errorData ||'Logout failed. Please try again.');
    }
  } catch (error) {
    //console.error('Error during logout:', error);
    alert('An error occurred. Please try again.');
  }
};
  
  /*const selectDisk = (device) => {
    setSelectedDisk(device);   
  };*/
  

  const deleteModesConfig = {
    standard: { 
      passes: 3, 
      color: 'bg-blue-500', 
      label: 'Standard Secure Delete',
      icon: Lock,
      level: 'N'
    },
    military: { 
      passes: 7, 
      color: 'bg-red-500', 
      label: 'DoD 5220.22-M',
      icon: Shield,
      level: 'M'
    },
    forensic: { 
      passes: 7, 
      color: 'bg-purple-500', 
      label: 'Forensic-Level Overwrite',
      icon: Crosshair,
      level: 'F'
    },
    gutmann: { 
      passes: 35, 
      color: 'bg-yellow-500', 
      label: 'Gutmann Method',
      icon: Trash2,
      level: 'G'
    },
    nist: { 
      passes: 7, 
      color: 'bg-green-500', 
      label: 'NIST 800-88',
      icon: Bolt,
      level: 'N'
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-100">
      

      {/* Header with gradient */}
      <header className="fixed top-0 left-0 w-full z-50">
          {/* Top status bar */}
          <div className="bg-gray-900 text-gray-400 text-sm py-1 px-4">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <span className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  Last scan: 2 hours ago
                </span>
                <span className="flex items-center text-green-400">
                  <Star className="w-4 h-4 mr-1" />
                  System Protected
                </span>
              </div>
              <div className="flex items-center space-x-4">
                <a href="#" className="hover:text-gray-200">Support</a>
                <a href="#" className="hover:text-gray-200">Documentation</a>
              </div>
            </div>
          </div>

          {isConfirmVisible && (
                <DeleteConfirmation
                    type={confirmationDetails.type} // Pass the type
                    deleteMode={confirmationDetails.deleteMode} // Pass the delete mode
                    onConfirm={() => handleConfirmation(true)}
                    onCancel={() => handleConfirmation(false)}
                />
            )}

            {/* Include the loader component */}
          {loaderVisible && (
            <div className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm z-50 transition-all duration-300">
              <SecureDeleteSimulator deletionSuccess={deletionSuccess} />
            </div>
          )}
      
      {/* Main header */}
      <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            {/* Logo and search */}

          <div className="flex items-center flex-1">
            <div className="flex items-center space-x-4">
              <div className="group">
                <Shield className="text-blue-500 group-hover:text-blue-400 transition-colors duration-200" size={32} />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-gray-900"></div>
              </div>
              <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400">
                SecureErase Pro
              </h1>
            </div>

            {/* Search bar */}
            <div className="ml-8 hidden md:block max-w-xl flex-1">
              <div className="">
                <input
                  type="text"
                  placeholder="Search secured files..."
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg pl-10 pr-4 py-2 text-sm text-gray-200 placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />               
              </div>
            </div>
          </div>

            {/* Right side controls */}
            <div className="flex items-center space-x-4">
              {/* Help */}
              
              <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-gray-200" onClick={toggleModal}>
                <HelpCircle size={20} />
              </button>
        

              {/* Notifications */}
              <div className="">
                <button
                  onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                  className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-gray-200 "
                >
                  <Bell size={20} />
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs flex items-center justify-center text-white">3</span>
                </button>

                {isNotificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-gray-800 rounded-lg shadow-lg py-2 border border-gray-700">
                    <div className="px-4 py-2 border-b border-gray-700 flex justify-between items-center">
                      <h3 className="font-medium text-gray-200">Notifications</h3>
                      <button className="text-xs text-blue-400 hover:text-blue-300">Mark all as read</button>
                    </div>
                    {notifications.map(notification => (
                      <div key={notification.id} className="px-4 py-3 hover:bg-gray-700 cursor-pointer">
                        <div className="flex justify-between items-start">
                          <p className="text-sm text-gray-200">{notification.message}</p>
                          <span className="text-xs text-gray-400">{notification.time}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Settings */}
              <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-gray-200">
                <Settings size={20} />
              </button>

              {/* Profile */}
              <div className="">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center gap-3 bg-gray-700 p-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                <div className="flex items-center gap-2">
                  {currentUser ? (
                     <>
                     <div className="h-8 w-8 rounded-full bg-blue-500  items-center justify-center">
                       <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                     </div>
                     <div className="flex flex-col">
                       <span className="text-sm font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                       <span className="text-xs text-gray-400">{currentUser.role}</span>
                     </div>
                   </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Skeleton /> {/* Skeleton for Avatar */}
                      <div className="flex flex-col">
                        <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                        <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                      </div>
                    </div>
                  )}
                </div>
                <ChevronDown size={16} className="text-gray-400" />
              </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-lg shadow-lg py-2 border border-gray-700">
                    <a href="#" className="px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 flex items-center space-x-2">
                      <User size={16} />
                      <span>Profile</span>
                    </a>
                    <a href="#" className="px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 flex items-center space-x-2">
                      <Settings size={16} />
                      <span>Settings</span>
                    </a>
                    <div className="border-t border-gray-700 my-1"></div>
                    <button className="w-full px-4 py-2 text-sm text-red-400 hover:bg-gray-700 flex items-center space-x-2"
                     onClick={handleLogout}
                     >
                       <LogOut className="h-4 w-4 mr-3" />
                       <span>Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
   
    
      {/* Modal */}
      {isModalOpen && (
         <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">      
             <HelpCenter onClose={onClose} />
          
        </div>
      )}


    <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="fixed left-0 top-16 w-72 bg-gray-900/95 backdrop-blur-md h-[calc(100vh-4rem)] shadow-xl border-r border-gray-800 z-40 overflow-y-auto overflow-x-hidden transition-all duration-200 ease-in-out">
          <Sidebar 
            deleteMode={deleteMode}
            setDeleteMode={setDeleteMode}
            selectedTarget={selectedTarget}
            handleTargetSelection={handleTargetSelection}
            deleteModesConfig={deleteModesConfig}
            performanceData={performanceData}
            optDuration={optDuration}
            deletionStats={deletionStats}
          />
        </aside>        

        {/* Main Content - adjust margin to match sidebar width */}
        <MainContent
              loading={loading}
              isLoading={isLoading}
              selectedTarget={selectedTarget}
              selectedFiles={selectedFiles}
              selectedFolders={selectedFolders}
              selectedDisks={selectedDisks}
              fileList={fileList}
              folderList={folderList}
              diskList={diskList}
              message={message}
              handleFileClick={handleFileClick}
              handleFolderInteraction={handleFolderInteraction}
              selectDisk={selectDisk}
              handleProcessClick={handleProcessClick}
              loaderVisible={loaderVisible}
              deletionSuccess={deletionSuccess}
              alert={alert}
              handleAlertClose={handleAlertClose}
              deletionStats={deletionStats}
              performanceData={performanceData}
              deleteModesConfig={deleteModesConfig}
              deleteMode={deleteMode}
            />
        
        </div>


       

      {/* Footer */}
     <Footer />
   
    </div>
    
  );
};

export default SecureDeletionInterface;





